import React from 'react';

const ResultsPagination = ({
  currentPage,
  totalPages,
  onGoToPage,
  onGoToFirstPage,
  onGoToLastPage,
  onGoToPreviousPage,
  onGoToNextPage
}) => {
  if (totalPages <= 1) return null;

  return (
    <div className="card-footer bg-white border-top">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={onGoToFirstPage}
            disabled={currentPage === 1}
            title="Primera página"
          >
            <i className="bi bi-chevron-double-left"></i>
          </button>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={onGoToPreviousPage}
            disabled={currentPage === 1}
            title="Página anterior"
          >
            <i className="bi bi-chevron-left"></i>
          </button>
        </div>
        
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">Página</span>
          <select 
            className="form-select form-select-sm" 
            style={{ width: 'auto' }}
            value={currentPage}
            onChange={(e) => onGoToPage(parseInt(e.target.value))}
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <option key={page} value={page}>{page}</option>
            ))}
          </select>
          <span className="text-muted small">de {totalPages}</span>
        </div>
        
        <div className="d-flex align-items-center gap-2">
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={onGoToNextPage}
            disabled={currentPage === totalPages}
            title="Página siguiente"
          >
            <i className="bi bi-chevron-right"></i>
          </button>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={onGoToLastPage}
            disabled={currentPage === totalPages}
            title="Última página"
          >
            <i className="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

const ResultsTable = ({
  extractionResults,
  selectedDocumentFields,
  currentResults,
  startIndex,
  endIndex,
  totalResults,
  currentPage,
  totalPages,
  onExportResults,
  onClearResults,
  onGoToPage,
  onGoToFirstPage,
  onGoToLastPage,
  onGoToPreviousPage,
  onGoToNextPage
}) => {
  // Si solo hay un resultado, asegúrate que currentResults lo incluya
  let displayResults = currentResults;
  if (extractionResults.length === 1) {
    displayResults = extractionResults;
  }
  if (extractionResults.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-table display-4 text-muted mb-3"></i>
        <h5 className="text-muted mb-2">No hay resultados de extracción</h5>
        <p className="text-muted">Los resultados aparecerán aquí después de procesar los documentos</p>
      </div>
    );
  }

  const exportButtons = [
    { format: 'excel', icon: 'bi-file-earmark-excel', class: 'btn-success', text: 'Excel' },
    { format: 'csv', icon: 'bi-filetype-csv', class: 'btn-info', text: 'CSV' },
    { format: 'json', icon: 'bi-filetype-json', class: 'btn-warning', text: 'JSON' }
  ];

  return (
    <div className="card border-0 shadow-sm results-table">
      <div className="card-header bg-white">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <h5 className="card-title mb-0 me-3">
              <i className="bi bi-table me-2 text-primary"></i>
              Resultados de Extracción
            </h5>
            <span className="badge bg-primary">{extractionResults.length}</span>
          </div>
          
          <div className="d-flex align-items-center gap-3">
            {extractionResults.length > 0 && (
              <small className="text-muted">
                Mostrando {startIndex + 1}-{Math.min(endIndex, totalResults)} de {totalResults} resultados
              </small>
            )}
            
            <div className="d-flex gap-2">
              {exportButtons.map(btn => (
                <button 
                  key={btn.format}
                  className={`btn btn-sm ${btn.class}`}
                  onClick={() => onExportResults(btn.format)}
                  title={`Exportar a ${btn.text}`}
                >
                  <i className={`${btn.icon} me-1`}></i>
                  {btn.text}
                </button>
              ))}
              <button 
                className="btn btn-sm btn-outline-danger"
                onClick={onClearResults}
                title="Limpiar resultados"
              >
                <i className="bi bi-trash me-1"></i>
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light sticky-top">
              <tr>
                <th className="px-4 py-3" style={{ minWidth: '180px' }}>
                  <i className="bi bi-file-earmark me-2"></i>Archivo
                </th>
                <th className="px-3 py-3" style={{ minWidth: '100px' }}>
                  Estado
                </th>
                {selectedDocumentFields.map(field => (
                  <th key={field.id} className="px-3 py-3" style={{ minWidth: '140px' }}>
                    <div className="d-flex align-items-center">
                      <span className="me-2">{field.nombre}</span>
                      <span className="badge bg-secondary" style={{ fontSize: '0.6rem' }}>
                        {field.tipo}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayResults.map((result, index) => (
                <tr key={result.id} className="align-middle">
                  <td className="px-4 py-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-file-earmark-text text-primary me-2"></i>
                      <span className="text-truncate fw-medium" title={result.fileName}>
                        {result.fileName}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {result.success ? (
                      <span className="badge bg-success"><i className="bi bi-check-circle me-1"></i>Éxito</span>
                    ) : (
                      <span className="badge bg-danger" title={result.error || 'Error'}>
                        <i className="bi bi-x-circle me-1"></i>Error
                      </span>
                    )}
                  </td>
                  {selectedDocumentFields.map(field => (
                    <td key={field.id} className="px-3 py-3">
                      <span 
                        className="truncated-text" 
                        title={result.extractedData[field.nombre] || '-'}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                      >
                        {result.extractedData[field.nombre] 
                          ? (result.extractedData[field.nombre].length > 50 
                              ? `${result.extractedData[field.nombre].substring(0, 50)}...` 
                              : result.extractedData[field.nombre]
                            )
                          : '-'
                        }
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <ResultsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onGoToPage={onGoToPage}
        onGoToFirstPage={onGoToFirstPage}
        onGoToLastPage={onGoToLastPage}
        onGoToPreviousPage={onGoToPreviousPage}
        onGoToNextPage={onGoToNextPage}
      />
    </div>
  );
};

export default ResultsTable;
